import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { SagaIterator } from 'redux-saga';
import progressService, {
    LearningJourneyResponse,
    LearningTopicsResponse,
    TodaySuggestionsResponse,
    SkillsProgressResponse,
    ProgressAnalyticsResponse,
    SuggestedLessonsResponse,
    TopicProgress,
    SkillProgress,
    LearningProgress,
    UpdateLearningProgressRequest,
    SuggestionFilters,
} from '@/services/progressService';
import {
    // Learning Journey
    fetchLearningJourneyRequest,
    fetchLearningJourneySuccess,
    fetchLearningJourneyFailure,
    updateLearningProgressRequest,
    updateLearningProgressSuccess,
    updateLearningProgressFailure,

    // Learning Topics
    fetchLearningTopicsRequest,
    fetchLearningTopicsSuccess,
    fetchLearningTopicsFailure,
    fetchTopicProgressRequest,
    fetchTopicProgressSuccess,
    fetchTopicProgressFailure,

    // Today's Suggestions
    fetchTodaySuggestionsRequest,
    fetchTodaySuggestionsSuccess,
    fetchTodaySuggestionsFailure,
    fetchSuggestedLessonsRequest,
    fetchSuggestedLessonsSuccess,
    fetchSuggestedLessonsFailure,

    // Skills Progress
    fetchSkillsProgressRequest,
    fetchSkillsProgressSuccess,
    fetchSkillsProgressFailure,
    updateSkillProgressRequest,
    updateSkillProgressSuccess,
    updateSkillProgressFailure,

    // Progress Analytics
    fetchProgressAnalyticsRequest,
    fetchProgressAnalyticsSuccess,
    fetchProgressAnalyticsFailure,
} from '@/store/slices/progressSlice';

// === LEARNING JOURNEY SAGAS ===
function* fetchLearningJourneySaga(): SagaIterator {
    try {
        const response: LearningJourneyResponse = yield call(progressService.getLearningJourney);
        yield put(fetchLearningJourneySuccess(response));
    } catch (error: any) {
        yield put(fetchLearningJourneyFailure(error.message || 'Failed to fetch learning journey'));
    }
}

function* updateLearningProgressSaga(action: PayloadAction<UpdateLearningProgressRequest>): SagaIterator {
    try {
        const response: LearningProgress = yield call(progressService.updateLearningProgress, action.payload);
        yield put(updateLearningProgressSuccess(response));
        // Refresh learning journey after updating progress
        yield put(fetchLearningJourneyRequest());
    } catch (error: any) {
        yield put(updateLearningProgressFailure(error.message || 'Failed to update learning progress'));
    }
}

// === LEARNING TOPICS SAGAS ===
function* fetchLearningTopicsSaga(): SagaIterator {
    try {
        const response: LearningTopicsResponse = yield call(progressService.getLearningTopics);
        yield put(fetchLearningTopicsSuccess(response));
    } catch (error: any) {
        yield put(fetchLearningTopicsFailure(error.message || 'Failed to fetch learning topics'));
    }
}

function* fetchTopicProgressSaga(action: PayloadAction<string>): SagaIterator {
    try {
        const response: TopicProgress = yield call(progressService.getTopicProgress, action.payload);
        yield put(fetchTopicProgressSuccess(response));
    } catch (error: any) {
        yield put(fetchTopicProgressFailure(error.message || 'Failed to fetch topic progress'));
    }
}

// === TODAY'S SUGGESTIONS SAGAS ===
function* fetchTodaySuggestionsSaga(): SagaIterator {
    try {
        const response: TodaySuggestionsResponse = yield call(progressService.getTodaySuggestions);
        yield put(fetchTodaySuggestionsSuccess(response));
    } catch (error: any) {
        yield put(fetchTodaySuggestionsFailure(error.message || 'Failed to fetch today\'s suggestions'));
    }
}

function* fetchSuggestedLessonsSaga(action: PayloadAction<SuggestionFilters | undefined>): SagaIterator {
    try {
        const response: SuggestedLessonsResponse = yield call(progressService.getSuggestedLessons, action.payload);
        yield put(fetchSuggestedLessonsSuccess(response));
    } catch (error: any) {
        yield put(fetchSuggestedLessonsFailure(error.message || 'Failed to fetch suggested lessons'));
    }
}

// === SKILLS PROGRESS SAGAS ===
function* fetchSkillsProgressSaga(): SagaIterator {
    try {
        const response: SkillsProgressResponse = yield call(progressService.getSkillsProgress);
        yield put(fetchSkillsProgressSuccess(response));
    } catch (error: any) {
        yield put(fetchSkillsProgressFailure(error.message || 'Failed to fetch skills progress'));
    }
}

function* updateSkillProgressSaga(action: PayloadAction<{ skillType: string; progress: number }>): SagaIterator {
    try {
        const { skillType, progress } = action.payload;
        const response: SkillProgress = yield call(progressService.updateSkillProgress, skillType, progress);
        yield put(updateSkillProgressSuccess(response));
        // Refresh learning journey after updating skill progress
        yield put(fetchLearningJourneyRequest());
    } catch (error: any) {
        yield put(updateSkillProgressFailure(error.message || 'Failed to update skill progress'));
    }
}

// === PROGRESS ANALYTICS SAGAS ===
function* fetchProgressAnalyticsSaga(action: PayloadAction<'week' | 'month' | 'year' | undefined>): SagaIterator {
    try {
        const response: ProgressAnalyticsResponse = yield call(progressService.getProgressAnalytics, action.payload);
        yield put(fetchProgressAnalyticsSuccess(response));
    } catch (error: any) {
        yield put(fetchProgressAnalyticsFailure(error.message || 'Failed to fetch progress analytics'));
    }
}

// === ROOT SAGA ===
export default function* progressSaga(): SagaIterator {
    yield takeLatest(fetchLearningJourneyRequest.type, fetchLearningJourneySaga);
    yield takeEvery(updateLearningProgressRequest.type, updateLearningProgressSaga);

    yield takeLatest(fetchLearningTopicsRequest.type, fetchLearningTopicsSaga);
    yield takeEvery(fetchTopicProgressRequest.type, fetchTopicProgressSaga);

    yield takeLatest(fetchTodaySuggestionsRequest.type, fetchTodaySuggestionsSaga);
    yield takeEvery(fetchSuggestedLessonsRequest.type, fetchSuggestedLessonsSaga);

    yield takeLatest(fetchSkillsProgressRequest.type, fetchSkillsProgressSaga);
    yield takeEvery(updateSkillProgressRequest.type, updateSkillProgressSaga);

    yield takeLatest(fetchProgressAnalyticsRequest.type, fetchProgressAnalyticsSaga);
} 